/**
 * Module-level registry of code-execution-generated images, keyed by filename.
 *
 * The chat stream emits two parallel signals for a generated chart/plot:
 *
 *   - a `tool-result-files` SSE event carrying the downloaded image bytes as a
 *     base64 data URL plus the sandbox filename (see `stream-events.ts`'s
 *     `emitGeneratedFiles`), which the SSE adapter (`upright-chat-adapter.ts`)
 *     writes both onto the code_execution tool-call's `result.data.images`
 *     (rendered inside the "Code execution" card) AND into this registry; and
 *   - the model's own inline markdown image in its prose, e.g.
 *     `![chart](nordic_population_2024.png)` — a BARE FILENAME, because the
 *     system prompt ("inline charts/images are the exception") tells the model
 *     a generated PNG/JPEG/SVG "is displayed directly in the chat".
 *
 * The `img` override in `upright-chat-markdown.tsx` resolves that bare filename
 * against this registry at render time and swaps in the data URL. Without it,
 * react-markdown emits a default `<img src="nordic_population_2024.png">` whose
 * bare relative URL 403s → broken-image icon (PLT-5148).
 *
 * Why a module-level Map (not React context) — same three reasons as
 * `citation-registry.ts`:
 *   1. The `img` component renders inside `react-markdown`'s tree (created via
 *      `makeMarkdownText`), where the per-message tool results aren't reachable
 *      as props — it has to look the image up by filename at render time.
 *   2. assistant-ui's `Text` component is shared across all messages in a
 *      thread; a per-message map would need a provider around each message,
 *      which isn't accessible from outside the assistant-ui tree.
 *   3. Filenames are the only key the markdown reference carries, so a global
 *      filename map is the natural lookup.
 *
 * Collision semantics: keyed by filename, last writer wins. Two charts both
 * saved as `chart.png` in one conversation would alias to the most recent — an
 * accepted limitation (matching the unbounded, last-wins nature of the citation
 * registry). The model is steered toward descriptive filenames, and the image
 * also always renders unambiguously in its own code_execution card.
 *
 * The map is unbounded for the tab's lifetime; each entry is one base64 data
 * URL (capped server-side at `MAX_GENERATED_FILE_BYTES` = 5MB raw).
 * `clearGeneratedImageRegistry` is exposed for tests / future hygiene.
 */

export interface RegisteredGeneratedImage {
  /** `data:<mimeType>;base64,<…>` — safe to use directly as an <img> src. */
  dataUrl: string;
  mimeType: string;
  filename: string;
}

const registry = new Map<string, RegisteredGeneratedImage>();

/**
 * Reduce a filename or markdown image reference to a bare basename for keying /
 * lookup. Strips any query/hash, any directory path, and any leading scheme
 * (e.g. some sandboxes reference files as `sandbox:chart.png` or
 * `attachment:/tmp/chart.png`). `chart.png`, `./out/chart.png`,
 * `sandbox:chart.png` all normalise to `chart.png`.
 */
export function generatedImageKey(ref: string): string {
  const noQuery = ref.split(/[?#]/)[0];
  const afterSlash = noQuery.split(/[\\/]/).pop() ?? noQuery;
  // A bare `scheme:name` (no slash) still leaves the scheme on `afterSlash`;
  // take the segment after the last colon to drop it. A normal `chart.png`
  // has no colon and is returned unchanged.
  return afterSlash.split(':').pop() ?? afterSlash;
}

/**
 * Register a generated image under both its exact filename and its normalised
 * basename, so a prose reference that adds/omits a path or scheme still
 * resolves.
 */
export function registerGeneratedImage(image: RegisteredGeneratedImage): void {
  if (!image.dataUrl || !image.filename) return;
  registry.set(image.filename, image);
  registry.set(generatedImageKey(image.filename), image);
}

/**
 * Look up a generated image by the `src` of a markdown image node. Tries the
 * raw value first (exact filename match), then the normalised basename.
 * Returns undefined when nothing matches — the caller renders a graceful
 * fallback rather than a broken-image icon.
 */
export function getGeneratedImage(src: string | undefined | null): RegisteredGeneratedImage | undefined {
  if (typeof src !== 'string' || src.length === 0) return undefined;
  return registry.get(src) ?? registry.get(generatedImageKey(src));
}

/** Whether `src` resolves to a registered generated image. */
export function isRegisteredGeneratedImage(src: string | undefined | null): boolean {
  return getGeneratedImage(src) !== undefined;
}

/**
 * Build a `react-markdown` `urlTransform` that lets a `src` resolving to a
 * registered generated image through unchanged, and delegates everything else
 * to `defaultTransform` (typically `defaultUrlTransform` from react-markdown).
 *
 * react-markdown's `defaultUrlTransform` strips any URL whose scheme isn't on
 * its safe-list (http/https/mailto/tel), replacing it with `""`. A bare
 * relative filename (`chart.png`) survives that already, but some sandboxes
 * reference a generated file with a scheme (`sandbox:chart.png`,
 * `attachment:/tmp/chart.png`) which the default transform would blank out
 * before the `img` override ever sees it. Short-circuiting registered
 * generated-image refs here keeps the original `src` intact so the override
 * can resolve it against the registry. Higher-order, mirroring
 * `makeCitationAwareUrlTransform`, to keep react-markdown out of this module's
 * import graph.
 */
export function makeGeneratedImageAwareUrlTransform(
  defaultTransform: (url: string) => string,
): (url: string) => string {
  return url => {
    if (isRegisteredGeneratedImage(url)) return url;
    return defaultTransform(url);
  };
}

/** Test-only helper. Production code should not rely on the registry being empty. */
export function clearGeneratedImageRegistry(): void {
  registry.clear();
}
